import React, { useEffect, useState } from 'react';
import { Table, Spinner, Alert, Card, Badge, Button, Modal, Form } from 'react-bootstrap';
import Navbar from './Navbar';
import API_CONFIG, { buildApiUrl, buildApiUrlWithParams, buildS3Url } from '../config/api';

const ImageMaintenance = () => {
    const [groups, setGroups] = useState([]);
    const [studentsWithImages, setStudentsWithImages] = useState([]);
    const [unmatchedFiles, setUnmatchedFiles] = useState([]);
    const [summary, setSummary] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedStudents, setSelectedStudents] = useState(new Set());
    const [showSelectedModal, setShowSelectedModal] = useState(false);
    const [resizing, setResizing] = useState(false);
    const [resizeResults, setResizeResults] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'image_size', direction: 'desc' });
    const [filterLargeImages, setFilterLargeImages] = useState(false);
    const [selectedOrphanedFiles, setSelectedOrphanedFiles] = useState(new Set());
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteResults, setDeleteResults] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (token) {
            const decoded = JSON.parse(atob(token.split(".")[1]));
            setGroups(decoded["cognito:groups"] || []);
        }
    }, []);

    useEffect(() => {
        if (groups.includes("mtkd-admin")) {
            fetchImageData();
        }
    }, [groups]);

    const fetchImageData = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            setError("No authentication token found.");
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const response = await fetch(
                buildApiUrl(API_CONFIG.ENDPOINTS.STUDENT_IMAGES),
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch image data: ${response.statusText}`);
            }

            const data = await response.json();
            setStudentsWithImages(data.students_with_images || []);
            setUnmatchedFiles(data.unmatched_s3_files || []);
            setSummary(data.summary || {});
        } catch (err) {
            console.error("Error fetching image data:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    const handleStudentSelect = (studentId) => {
        const student = studentsWithImages.find(s => s.student_id === studentId);
        if (!hasAtLeastOneImage(student)) {
            return; // Don't allow selection if no images
        }
        
        setSelectedStudents(prev => {
            const newSet = new Set(prev);
            if (newSet.has(studentId)) {
                newSet.delete(studentId);
            } else {
                newSet.add(studentId);
            }
            return newSet;
        });
    };

    const handleSelectAll = () => {
        const studentsWithImagesOnly = getStudentsWithImages();
        if (selectedStudents.size === studentsWithImagesOnly.length) {
            setSelectedStudents(new Set());
        } else {
            setSelectedStudents(new Set(studentsWithImagesOnly.map(student => student.student_id)));
        }
    };

    const handleActionButton = () => {
        setShowSelectedModal(true);
    };

    const getSelectedStudentsData = () => {
        return studentsWithImages.filter(student => selectedStudents.has(student.student_id));
    };

    const hasAtLeastOneImage = (student) => {
        return student.student_pic_file || student.release_pic_file;
    };

    const getStudentsWithImages = () => {
        let filteredStudents = studentsWithImages.filter(student => hasAtLeastOneImage(student));
        
        // Apply large image filter if enabled (>= 1MB = 1,048,576 bytes)
        if (filterLargeImages) {
            console.log(`Filtering students with student_pic_file >= 1MB. Total students with images: ${filteredStudents.length}`);
            const beforeCount = filteredStudents.length;
            filteredStudents = filteredStudents.filter(student => {
                const studentPicSize = student.student_pic_file?.size || 0;
                const passes = studentPicSize >= 1048576;
                console.log(`Student ${student.student_id}: student_pic_file=${studentPicSize} bytes (${formatFileSize(studentPicSize)}), passes=${passes}`);
                return passes;
            });
            console.log(`After filtering: ${filteredStudents.length} students (was ${beforeCount})`);
        }
        
        return filteredStudents;
    };

    const getStudentImageSize = (student) => {
        let totalSize = 0;
        if (student.student_pic_file) {
            totalSize += student.student_pic_file.size;
        }
        if (student.release_pic_file) {
            totalSize += student.release_pic_file.size;
        }
        return totalSize;
    };

    const sortStudents = (students) => {
        return [...students].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortConfig.key) {
                case 'image_size':
                    aValue = getStudentImageSize(a);
                    bValue = getStudentImageSize(b);
                    break;
                case 'name':
                    aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
                    bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
                    break;
                case 'student_id':
                    aValue = parseInt(a.student_id);
                    bValue = parseInt(b.student_id);
                    break;
                default:
                    return 0;
            }
            
            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const handleSort = (key) => {
        setSortConfig(prevConfig => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handleResizeImages = async () => {
        const selectedStudentsData = getSelectedStudentsData();
        
        // Extract student picture data with student IDs
        const studentPicData = selectedStudentsData
            .filter(student => student.student_pic_file)
            .map(student => ({
                student_id: student.student_id,
                guid: student.student_pic_file.key
            }));

        if (studentPicData.length === 0) {
            alert('No student pictures found in selected students.');
            return;
        }

        const token = localStorage.getItem("token");
        if (!token) {
            setError("No authentication token found.");
            return;
        }

        try {
            setResizing(true);
            setResizeResults(null);

            const response = await fetch(
                buildApiUrl(API_CONFIG.ENDPOINTS.STUDENT_IMAGES_RESIZE),
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        images: studentPicData
                    })
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to resize images: ${response.statusText}`);
            }

            const data = await response.json();
            setResizeResults(data);
            setShowSelectedModal(false);
            
            // Refresh the image data to show updated information
            await fetchImageData();
            
        } catch (err) {
            console.error("Error resizing images:", err);
            setError(err.message);
        } finally {
            setResizing(false);
        }
    };

    const handleOrphanedFileSelect = (fileKey) => {
        setSelectedOrphanedFiles(prev => {
            const newSet = new Set(prev);
            if (newSet.has(fileKey)) {
                newSet.delete(fileKey);
            } else {
                newSet.add(fileKey);
            }
            return newSet;
        });
    };

    const handleSelectAllOrphaned = () => {
        if (selectedOrphanedFiles.size === unmatchedFiles.length) {
            setSelectedOrphanedFiles(new Set());
        } else {
            setSelectedOrphanedFiles(new Set(unmatchedFiles.map(file => file.key)));
        }
    };

    const handleDeleteOrphanedFiles = async () => {
        if (selectedOrphanedFiles.size === 0) {
            alert('No files selected for deletion.');
            return;
        }

        const token = localStorage.getItem("token");
        if (!token) {
            setError("No authentication token found.");
            return;
        }

        try {
            setDeleting(true);
            setDeleteResults(null);

            const deletePromises = Array.from(selectedOrphanedFiles).map(async (fileKey) => {
                try {
                    const response = await fetch(
                        buildApiUrlWithParams(API_CONFIG.ENDPOINTS.DELETE_ORPHANED_IMAGE, fileKey),
                        {
                            method: "DELETE",
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                            }
                        }
                    );

                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.error || `Failed to delete ${fileKey}: ${response.statusText}`);
                    }

                    const data = await response.json();
                    return {
                        fileKey,
                        success: true,
                        message: data.message
                    };
                } catch (err) {
                    return {
                        fileKey,
                        success: false,
                        error: err.message
                    };
                }
            });

            const results = await Promise.all(deletePromises);
            const successful = results.filter(r => r.success);
            const failed = results.filter(r => !r.success);

            setDeleteResults({
                successful,
                failed,
                summary: {
                    total_requested: results.length,
                    successful: successful.length,
                    failed: failed.length
                }
            });

            setShowDeleteModal(false);
            setSelectedOrphanedFiles(new Set());
            
            // Refresh the image data to show updated information
            await fetchImageData();
            
        } catch (err) {
            console.error("Error deleting orphaned files:", err);
            setError(err.message);
        } finally {
            setDeleting(false);
        }
    };

    // Check if user has admin access
    if (!groups.includes("mtkd-admin")) {
        return (
            <div>
                <Navbar groups={groups} />
                <div className="container mt-4">
                    <div className="alert alert-danger" role="alert">
                        <h4 className="alert-heading">Access Denied</h4>
                        <p>You do not have permission to access this page. Admin privileges are required.</p>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div>
                <Navbar groups={groups} />
                <div className="container mt-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <span className="ms-3">Loading image data...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <Navbar groups={groups} />
                <div className="container mt-4">
                    <Alert variant="danger">
                        <Alert.Heading>Error Loading Image Data</Alert.Heading>
                        <p>{error}</p>
                        <button className="btn btn-outline-danger" onClick={fetchImageData}>
                            Try Again
                        </button>
                    </Alert>
                </div>
            </div>
        );
    }

    return (
        <div>
            <Navbar groups={groups} />
            <div className="container mt-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2>Image Maintenance</h2>
                    <button className="btn btn-outline-primary" onClick={fetchImageData}>
                        Refresh Data
                    </button>
                </div>

                {/* Summary Cards */}
                <div className="row mb-4">
                    <div className="col-md-3">
                        <Card className="text-center">
                            <Card.Body>
                                <Card.Title className="text-primary">{summary.total_students || 0}</Card.Title>
                                <Card.Text>Total Students</Card.Text>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-md-3">
                        <Card className="text-center">
                            <Card.Body>
                                <Card.Title className="text-success">{summary.total_s3_files || 0}</Card.Title>
                                <Card.Text>Total S3 Files</Card.Text>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-md-3">
                        <Card className="text-center">
                            <Card.Body>
                                <Card.Title className="text-info">{summary.matched_files || 0}</Card.Title>
                                <Card.Text>Matched Files</Card.Text>
                            </Card.Body>
                        </Card>
                    </div>
                    <div className="col-md-3">
                        <Card className="text-center">
                            <Card.Body>
                                <Card.Title className="text-warning">{summary.unmatched_files || 0}</Card.Title>
                                <Card.Text>Orphaned Files</Card.Text>
                            </Card.Body>
                        </Card>
                    </div>
                </div>

                {/* Students with Images Table */}
                <Card className="mb-4">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <h5 className="mb-0 me-3">Students with Images</h5>
                            <Form.Check
                                type="checkbox"
                                id="filterLargeImages"
                                label={
                                    <span>
                                        Show only student pics ≥ 1MB
                                        {filterLargeImages && (
                                            <Badge bg="info" className="ms-2">Filter Active</Badge>
                                        )}
                                    </span>
                                }
                                checked={filterLargeImages}
                                onChange={(e) => setFilterLargeImages(e.target.checked)}
                                className="text-muted"
                            />
                        </div>
                        <div>
                            <span className="text-muted me-3">
                                {selectedStudents.size} of {getStudentsWithImages().length} selected (students with images)
                            </span>
                            <Button 
                                variant="primary" 
                                size="sm"
                                onClick={handleActionButton}
                                disabled={selectedStudents.size === 0}
                            >
                                Action on Selected
                            </Button>
                        </div>
                    </Card.Header>
                    <Card.Body>
                        <Table striped bordered hover responsive>
                            <thead>
                                <tr>
                                    <th>
                                        <Form.Check
                                            type="checkbox"
                                            checked={selectedStudents.size === getStudentsWithImages().length && getStudentsWithImages().length > 0}
                                            onChange={handleSelectAll}
                                            label={`Select All (${getStudentsWithImages().length} with images)`}
                                        />
                                    </th>
                                    <th 
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('student_id')}
                                    >
                                        Student ID
                                        {sortConfig.key === 'student_id' && (
                                            <span className="ms-1">
                                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </th>
                                    <th 
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('name')}
                                    >
                                        Name
                                        {sortConfig.key === 'name' && (
                                            <span className="ms-1">
                                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </th>
                                    <th>Nick Name</th>
                                    <th>Active</th>
                                    <th 
                                        style={{ cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => handleSort('image_size')}
                                    >
                                        Student Picture
                                        {sortConfig.key === 'image_size' && (
                                            <span className="ms-1">
                                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                            </span>
                                        )}
                                    </th>
                                    <th>Release Picture</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortStudents(getStudentsWithImages()).map((student) => (
                                    <tr key={student.student_id} className={!hasAtLeastOneImage(student) ? "table-secondary" : ""}>
                                        <td>
                                            <Form.Check
                                                type="checkbox"
                                                checked={selectedStudents.has(student.student_id)}
                                                onChange={() => handleStudentSelect(student.student_id)}
                                                disabled={!hasAtLeastOneImage(student)}
                                                title={!hasAtLeastOneImage(student) ? "Student has no images" : ""}
                                            />
                                        </td>
                                        <td>{student.student_id}</td>
                                        <td>{student.first_name} {student.last_name}</td>
                                        <td>{student.nick_name || '-'}</td>
                                        <td>
                                            <Badge bg={student.active ? 'success' : 'secondary'}>
                                                {student.active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </td>
                                        <td>
                                            {student.student_pic_file ? (
                                                <div>
                                                    <a 
                                                        href={buildS3Url(student.student_pic_file.key)} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="text-decoration-none"
                                                    >
                                                        <img 
                                                            src={buildS3Url(student.student_pic_file.key)} 
                                                            alt="Student" 
                                                            style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                            className="rounded"
                                                        />
                                                    </a>
                                                    <div className="small text-muted mt-1">
                                                        {formatFileSize(student.student_pic_file.size)}
                                                    </div>
                                                    <div className="small text-info">
                                                        Total: {formatFileSize(getStudentImageSize(student))}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-muted">No image</span>
                                            )}
                                        </td>
                                        <td>
                                            {student.release_pic_file ? (
                                                <div>
                                                    <a 
                                                        href={buildS3Url(student.release_pic_file.key)} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="text-decoration-none"
                                                    >
                                                        <img 
                                                            src={buildS3Url(student.release_pic_file.key)} 
                                                            alt="Release" 
                                                            style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                            className="rounded"
                                                        />
                                                    </a>
                                                    <div className="small text-muted mt-1">
                                                        {formatFileSize(student.release_pic_file.size)}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-muted">No image</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>

                {/* Orphaned Files Table */}
                <Card>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="mb-0">Orphaned S3 Files</h5>
                            <small className="text-muted">Files in S3 that don't match any student GUIDs</small>
                        </div>
                        <div>
                            <span className="text-muted me-3">
                                {selectedOrphanedFiles.size} of {unmatchedFiles.length} selected
                            </span>
                            <Button 
                                variant="danger" 
                                size="sm"
                                onClick={() => setShowDeleteModal(true)}
                                disabled={selectedOrphanedFiles.size === 0}
                            >
                                Delete Selected
                            </Button>
                        </div>
                    </Card.Header>
                    <Card.Body>
                        <Table striped bordered hover responsive>
                            <thead>
                                <tr>
                                    <th>
                                        <Form.Check
                                            type="checkbox"
                                            checked={selectedOrphanedFiles.size === unmatchedFiles.length && unmatchedFiles.length > 0}
                                            onChange={handleSelectAllOrphaned}
                                            label={`Select All (${unmatchedFiles.length})`}
                                        />
                                    </th>
                                    <th>File Name</th>
                                    <th>Size</th>
                                    <th>Last Modified</th>
                                    <th>Preview</th>
                                </tr>
                            </thead>
                            <tbody>
                                {unmatchedFiles.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="text-center text-muted">
                                            No orphaned files found
                                        </td>
                                    </tr>
                                ) : (
                                    unmatchedFiles.map((file, index) => (
                                        <tr key={index}>
                                            <td>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={selectedOrphanedFiles.has(file.key)}
                                                    onChange={() => handleOrphanedFileSelect(file.key)}
                                                />
                                            </td>
                                            <td>
                                                <code>{file.key}</code>
                                            </td>
                                            <td>{formatFileSize(file.size)}</td>
                                            <td>{formatDate(file.last_modified)}</td>
                                            <td>
                                                <a 
                                                    href={buildS3Url(file.key)} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="text-decoration-none"
                                                >
                                                    <img 
                                                        src={buildS3Url(file.key)} 
                                                        alt="Orphaned file" 
                                                        style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                        className="rounded"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'inline';
                                                        }}
                                                    />
                                                    <span style={{ display: 'none' }} className="text-muted small">
                                                        View File
                                                    </span>
                                                </a>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>

                {/* Selected Students Modal */}
                <Modal show={showSelectedModal} onHide={() => setShowSelectedModal(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Selected Students</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p className="mb-3">
                            <strong>{selectedStudents.size}</strong> student(s) selected:
                        </p>
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th>Student ID</th>
                                    <th>Name</th>
                                    <th>Nick Name</th>
                                    <th>Active</th>
                                    <th>Has Student Pic</th>
                                    <th>Has Release Pic</th>
                                </tr>
                            </thead>
                            <tbody>
                                {getSelectedStudentsData().map((student) => (
                                    <tr key={student.student_id}>
                                        <td>{student.student_id}</td>
                                        <td>{student.first_name} {student.last_name}</td>
                                        <td>{student.nick_name || '-'}</td>
                                        <td>
                                            <Badge bg={student.active ? 'success' : 'secondary'}>
                                                {student.active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge bg={student.student_pic_file ? 'success' : 'secondary'}>
                                                {student.student_pic_file ? 'Yes' : 'No'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge bg={student.release_pic_file ? 'success' : 'secondary'}>
                                                {student.release_pic_file ? 'Yes' : 'No'}
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowSelectedModal(false)}>
                            Close
                        </Button>
                        <Button 
                            variant="primary" 
                            onClick={handleResizeImages}
                            disabled={resizing}
                        >
                            {resizing ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Resizing Images...
                                </>
                            ) : (
                                'Resize Student Pictures'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Resize Results Modal */}
                <Modal show={resizeResults !== null} onHide={() => setResizeResults(null)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Image Resize Results</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {resizeResults && (
                            <div>
                                <div className="row mb-3">
                                    <div className="col-md-3">
                                        <Card className="text-center">
                                            <Card.Body>
                                                <Card.Title className="text-primary">{resizeResults.summary.total_requested}</Card.Title>
                                                <Card.Text>Total Requested</Card.Text>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                    <div className="col-md-3">
                                        <Card className="text-center">
                                            <Card.Body>
                                                <Card.Title className="text-success">{resizeResults.summary.successful}</Card.Title>
                                                <Card.Text>Successfully Resized</Card.Text>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                    <div className="col-md-3">
                                        <Card className="text-center">
                                            <Card.Body>
                                                <Card.Title className="text-info">{resizeResults.summary.database_updates_successful}</Card.Title>
                                                <Card.Text>Database Updated</Card.Text>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                    <div className="col-md-3">
                                        <Card className="text-center">
                                            <Card.Body>
                                                <Card.Title className="text-danger">{resizeResults.summary.failed}</Card.Title>
                                                <Card.Text>Failed</Card.Text>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                </div>

                                {resizeResults.successful_resizes.length > 0 && (
                                    <div className="mb-4">
                                        <h6 className="text-success">Successfully Resized Images:</h6>
                                        <Table striped bordered hover size="sm">
                                            <thead>
                                                <tr>
                                                    <th>Student ID</th>
                                                    <th>Original File</th>
                                                    <th>Resized File</th>
                                                    <th>Size Reduction</th>
                                                    <th>Database Update</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {resizeResults.successful_resizes.map((result, index) => (
                                                    <tr key={index}>
                                                        <td>{result.student_id}</td>
                                                        <td>
                                                            <code>{result.original_guid}</code>
                                                        </td>
                                                        <td>
                                                            <code>{result.resized_guid}</code>
                                                        </td>
                                                        <td>
                                                            <Badge bg="success">
                                                                {Math.round((1 - result.resized_size / result.original_size) * 100)}% smaller
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <Badge bg={result.database_update?.success ? "success" : "danger"}>
                                                                {result.database_update?.success ? "Updated" : "Failed"}
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}

                                {resizeResults.failed_resizes.length > 0 && (
                                    <div>
                                        <h6 className="text-danger">Failed Resizes:</h6>
                                        <Table striped bordered hover size="sm">
                                            <thead>
                                                <tr>
                                                    <th>File</th>
                                                    <th>Error</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {resizeResults.failed_resizes.map((result, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <code>{result.guid}</code>
                                                        </td>
                                                        <td className="text-danger">
                                                            {result.error}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" onClick={() => setResizeResults(null)}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Deletion</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p className="mb-3">
                            Are you sure you want to delete <strong>{selectedOrphanedFiles.size}</strong> orphaned file(s)?
                        </p>
                        <p className="text-danger mb-3">
                            <strong>Warning:</strong> This action cannot be undone. The files will be permanently deleted from S3.
                        </p>
                        <div className="mb-3">
                            <h6>Files to be deleted:</h6>
                            <ul className="list-unstyled">
                                {Array.from(selectedOrphanedFiles).map(fileKey => (
                                    <li key={fileKey}>
                                        <code>{fileKey}</code>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button 
                            variant="danger" 
                            onClick={handleDeleteOrphanedFiles}
                            disabled={deleting}
                        >
                            {deleting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Deleting...
                                </>
                            ) : (
                                'Delete Files'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Delete Results Modal */}
                <Modal show={deleteResults !== null} onHide={() => setDeleteResults(null)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Delete Results</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {deleteResults && (
                            <div>
                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <Card className="text-center">
                                            <Card.Body>
                                                <Card.Title className="text-primary">{deleteResults.summary.total_requested}</Card.Title>
                                                <Card.Text>Total Requested</Card.Text>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                    <div className="col-md-4">
                                        <Card className="text-center">
                                            <Card.Body>
                                                <Card.Title className="text-success">{deleteResults.summary.successful}</Card.Title>
                                                <Card.Text>Successfully Deleted</Card.Text>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                    <div className="col-md-4">
                                        <Card className="text-center">
                                            <Card.Body>
                                                <Card.Title className="text-danger">{deleteResults.summary.failed}</Card.Title>
                                                <Card.Text>Failed</Card.Text>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                </div>

                                {deleteResults.successful.length > 0 && (
                                    <div className="mb-4">
                                        <h6 className="text-success">Successfully Deleted Files:</h6>
                                        <Table striped bordered hover size="sm">
                                            <thead>
                                                <tr>
                                                    <th>File Name</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {deleteResults.successful.map((result, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <code>{result.fileKey}</code>
                                                        </td>
                                                        <td>
                                                            <Badge bg="success">Deleted</Badge>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}

                                {deleteResults.failed.length > 0 && (
                                    <div>
                                        <h6 className="text-danger">Failed Deletions:</h6>
                                        <Table striped bordered hover size="sm">
                                            <thead>
                                                <tr>
                                                    <th>File Name</th>
                                                    <th>Error</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {deleteResults.failed.map((result, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <code>{result.fileKey}</code>
                                                        </td>
                                                        <td className="text-danger">
                                                            {result.error}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" onClick={() => setDeleteResults(null)}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default ImageMaintenance;
